import { Flex, Text } from "@mantine/core";
import { useDisclosure, useElementSize, useMergedRef } from "@mantine/hooks";
import clsx from "clsx";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classes from "./ExpandableText.module.css";

interface ExpandableTextProps {
  text?: string | null;
  size?: "xs" | "sm" | "md";
  openByDefault?: boolean;
  className?: string;
  lineClamp?: number;
  inlineToggle?: boolean;
  opened?: boolean;
  onToggle?: () => void;
  withShowLess?: boolean;
  onOverflowChange?: (hasOverflow: boolean) => void;
}

const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  size = "sm",
  openByDefault = false,
  className,
  lineClamp = 1,
  inlineToggle = false,
  opened: controlledOpened,
  onToggle,
  withShowLess = true,
  onOverflowChange,
}) => {
  const { t } = useTranslation("shippingManagement");

  const elementRef = useRef<HTMLElement>(null);
  const { ref: sizeRef, height, width } = useElementSize();
  const textRef = useMergedRef(elementRef, sizeRef);

  const [uncontrolledOpened, { toggle: uncontrolledToggle }] =
    useDisclosure(openByDefault);

  const isControlled = controlledOpened !== undefined;
  const opened = isControlled ? controlledOpened : uncontrolledOpened;
  const toggle = () => (isControlled ? onToggle?.() : uncontrolledToggle());

  const [hasSpoiler, setHasSpoiler] = useState(false);

  const toggleSpoiler = () => {
    if (!hasSpoiler && !opened) {
      return;
    }
    toggle();
  };

  const handleToggleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  useEffect(() => {
    if (elementRef.current === null || height === 0 || opened) return;

    const handleChangeHasSpoiler = () => {
      const hasOverflow = elementRef.current!.scrollHeight > height + 1;
      setHasSpoiler(hasOverflow);
      onOverflowChange?.(hasOverflow);
    };

    handleChangeHasSpoiler();
  }, [height, width, opened]);

  if (!text) return null;

  if (inlineToggle) {
    const isClamped = !opened && hasSpoiler;

    return (
      <div className={classes.inlineRoot}>
        <Text
          ref={textRef}
          fz={size}
          lineClamp={opened ? undefined : lineClamp}
          className={clsx(
            classes.inlineText,
            { [classes.clamped]: isClamped },
            className
          )}
        >
          {isClamped && (
            <Text
              component="button"
              type="button"
              onClick={handleToggleClick}
              fz={size}
              fw={600}
              c="blue.4"
              className={clsx(classes.floatingToggle, "ml-1 cursor-pointer")}
            >
              &#91;{t("readMore")}&#93;
            </Text>
          )}
          {text}
          {opened && withShowLess && (
            <Text
              component="button"
              type="button"
              onClick={handleToggleClick}
              fz={size}
              fw={600}
              c="gray"
              className="ml-1 cursor-pointer"
            >
              &#91;{t("showLess", { ns: "common" })}&#93;
            </Text>
          )}
        </Text>
      </div>
    );
  }

  return (
    <Flex align={"baseline"} gap={4} flex={1} className="min-w-0">
      <Text
        flex={1}
        ref={textRef}
        fz={size}
        lh={1}
        lineClamp={opened ? undefined : lineClamp}
        onClick={toggleSpoiler}
        className={clsx(
          "min-w-0 wrap-anywhere",
          {
            "cursor-pointer": hasSpoiler || opened,
            "hover:bg-gray-lightest max-w-fit rounded": !opened && hasSpoiler,
            "bg-gray-lightest rounded": opened,
          },
          className
        )}
      >
        {text}
      </Text>
      {!opened && hasSpoiler && (
        <Text
          component={"button"}
          onClick={toggleSpoiler}
          fz={size}
          lh={1}
          fw={600}
          c={"blue.4"}
          className="shrink-0 cursor-pointer"
        >
          &#91;{t("readMore")}&#93;
        </Text>
      )}
    </Flex>
  );
};

export default ExpandableText;
